import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, MagnifyingGlass, QrCode, X, ClockCounterClockwise } from "@phosphor-icons/react";
import InvoicedToggle from "@/components/InvoicedToggle";

function StatusPill({ status }) {
  if (status === "open") {
    return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15 border border-amber-500/30 font-mono text-[10px] uppercase">Aperto</Badge>;
  }
  return <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 border border-emerald-500/30 font-mono text-[10px] uppercase">Chiuso</Badge>;
}

export default function WorkshopInterventionsPage() {
  useDocumentTitle("Interventi laboratorio");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.status = filter;
    if (q) params.q = q;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await api.get("/interventions", { params });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter, dateFrom, dateTo]);

  const clearDates = () => { setDateFrom(""); setDateTo(""); };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="workshop-interventions-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// LABORATORIO</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight flex items-center gap-3">
            <ClockCounterClockwise size={36} weight="duotone" className="text-primary" />
            Interventi laboratorio
          </h1>
          <p className="text-muted-foreground mt-2">Ricerca, crea e gestisci gli interventi in officina — sia aperti che chiusi.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/ricerca">
            <Button variant="outline" data-testid="workshop-scan-btn" className="rounded-full gap-2">
              <QrCode size={16} /> Scansiona
            </Button>
          </Link>
          <Link to="/interventi/nuovo">
            <Button data-testid="workshop-new-btn" className="rounded-full gap-2">
              <Plus size={16} weight="bold" /> Nuovo intervento
            </Button>
          </Link>
        </div>
      </header>

      <Card className="p-4 border-border/70 rounded-lg mb-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome, cognome, telefono, codice, modello…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="workshop-search-input"
              className="pl-9"
            />
          </div>

          <div className="flex gap-2 items-center">
            <div className="flex items-center gap-1">
              <label className="font-mono text-[10px] uppercase text-muted-foreground">Da</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" className="w-40" />
            </div>
            <div className="flex items-center gap-1">
              <label className="font-mono text-[10px] uppercase text-muted-foreground">A</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" className="w-40" />
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={clearDates} data-testid="date-clear" className="h-9">
                <X size={14} />
              </Button>
            )}
          </div>

          <div className="flex gap-1 bg-secondary rounded-md p-1">
            {[
              { k: "all", l: "Tutti" },
              { k: "open", l: "Aperti" },
              { k: "closed", l: "Chiusi" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`filter-${f.k}`}
                className={`px-4 py-1.5 rounded text-sm font-medium ${
                  filter === f.k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
              >
                {f.l}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={load} data-testid="workshop-refresh-btn">Aggiorna</Button>
        </div>
      </Card>

      <Card className="border-border/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Codice</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Prodotto</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Lavoro</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Stato</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Fattura</th>
              </tr>
            </thead>
            <tbody data-testid="interventions-table-body">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Caricamento…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  Nessun intervento trovato. <Link to="/interventi/nuovo" className="text-primary underline underline-offset-4">Creane uno</Link>.
                </td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border/50 hover:bg-secondary/40"
                    style={{ transition: "background-color 0.15s ease" }}>
                  <td className="px-4 py-3">
                    <Link to={`/interventi/${it.id}`} className="font-mono text-xs text-primary hover:underline" data-testid={`intervention-link-${it.code}`}>
                      {it.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.customer_surname} {it.customer_name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{it.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{it.product_type}</div>
                    <div className="text-xs text-muted-foreground">{[it.product_brand, it.product_model].filter(Boolean).join(" ")}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[280px]"><div className="truncate">{it.work_description}</div></td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    <div>{new Date(it.created_at).toLocaleDateString("it-IT")}</div>
                    <div className="text-[10px]">{new Date(it.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={it.status} /></td>
                  <td className="px-4 py-3">
                    <InvoicedToggle
                      intervention={it}
                      onChange={(u) => setItems((prev) => prev.map((x) => x.id === u.id ? u : x))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
